import { Box, IconButton, Stack, Typography } from '@mui/material'
import copy from 'copy-to-clipboard'
import moment from 'moment'
import React from 'react'
import { getImageSrc } from '../../../helpers/getImageSrc'
import {
  formatAddress,
  formatName,
  formatPhoneNumber,
} from '../../../helpers/ordersUtiles'
import {
  getIconFinancialSource,
  getTitleFinancialSource,
} from '../../../helpers/switchFinancialSource'
import { getComp } from '../../../utils/utils/DI'
import { formatSN } from '../../../utils/utils/formatSN'
import { CustomerPersonalInfo } from '../../Customers/types/customer-filters.types'
import { formatEquipmentId } from '../components/BodyElements/helpers/body-helpers'
import { RequestType } from '../types/Request.type'

export const mapChipValue = (
  request: RequestType | CustomerPersonalInfo,
  key: keyof RequestType,
) => {
  const statusStore = getComp('StatusesStore')
  const dealerStore = getComp('DealersStore')
  const { getStatusName, getStatusImage, getReportStatusImage } = statusStore

  switch (key) {
    case 'installationDate':
      return (request as RequestType).installationDate ? (
        <Stack direction={'row'} alignItems={'center'} spacing={'4px'}>
          {((request as RequestType).isExpiredAssign ||
            (request as RequestType).isExpiredInstall) && (
            <img src={getImageSrc('warn')} />
          )}
          <Typography variant='body1'>
            {moment((request as RequestType).installationDate).format(
              'DD.MM.YYYY',
            )}
          </Typography>
          <IconButton
            onClick={() => {
              copy(
                moment((request as RequestType).installationDate).format(
                  'DD.MM.YYYY',
                ),
              )
            }}
            sx={{ padding: '2px' }}
          >
            <Box
              component='img'
              src={getImageSrc('copy')}
              alt='Скопировать'
              sx={{
                width: '20px',
                height: '20px',
                opacity: '.6',
                '&:hover': {
                  opacity: '.8',
                },
              }}
            />
          </IconButton>
        </Stack>
      ) : (
        <Box width={'100px'} height={'20px'} />
      )

    case 'created':
      return (request as RequestType).created ? (
        moment((request as RequestType).created).format('DD.MM.YYYY')
      ) : (
        <Box width={'100px'} height={'20px'} />
      )

    case 'financialSourceId':
      const isEmpty =
        !(request as RequestType).financialSourceId &&
        (request as RequestType).financialSourceId !== 4

      return !isEmpty ? (
        <Stack direction={'row'} alignItems={'center'} spacing={'4px'}>
          <img
            src={getIconFinancialSource(
              (request as RequestType).financialSourceId,
            )}
          />
          <Typography variant='body1'>
            {getTitleFinancialSource(
              (request as RequestType).financialSourceId,
            )}
          </Typography>
        </Stack>
      ) : (
        <Box width={'100px'} height={'20px'} />
      )

    case 'status':
      return (
        <Stack direction='row' spacing={'4px'} alignItems={'center'}>
          <img src={getStatusImage((request as RequestType).status)} />
          <Typography variant='body1'>
            {getStatusName((request as RequestType).status)}
          </Typography>
        </Stack>
      )

    case 'reportId':
      return (request as RequestType)[key] ? (
        <Stack alignItems={'center'} direction='row' spacing={'4px'}>
          <img
            src={getReportStatusImage((request as RequestType).reportStatus)}
          />
          <Typography variant='body1'>
            №{(request as RequestType)?.[key]}
          </Typography>
          <IconButton
            onClick={() => {
              copy(String((request as RequestType)?.[key]))
            }}
            sx={{ padding: '2px' }}
          >
            <Box
              component='img'
              src={getImageSrc('copy')}
              alt='Скопировать'
              sx={{
                width: '20px',
                height: '20px',
                opacity: '.6',
                '&:hover': {
                  opacity: '.8',
                },
              }}
            />
          </IconButton>
        </Stack>
      ) : (
        <Box width={'100px'} height={'20px'} />
      )

    case 'dealer':
      return (request as RequestType).dealer ? (
        <Stack alignItems={'center'} direction='row' spacing={'4px'}>
          <img src={getImageSrc('dealer-small')} />
          <Typography variant='body1'>
            {
              dealerStore.getDealer((request as RequestType).dealer)
                ?.displayName
            }
          </Typography>
        </Stack>
      ) : (
        <Box width={'150px'} height={'20px'} />
      )

    // @ts-ignore
    case 'fullName':
      const fullName = formatName((request as CustomerPersonalInfo).fullName)
      return (
        <Stack alignItems={'center'} direction='row' spacing={'8px'}>
          <img src={getImageSrc('dealer-small')} />
          <Typography variant='body1'>{fullName}</Typography>
          <IconButton
            onClick={() => {
              copy(fullName)
            }}
            sx={{ padding: '2px' }}
          >
            <Box
              component='img'
              src={getImageSrc('copy')}
              alt='Скопировать'
              sx={{
                width: '20px',
                height: '20px',
                opacity: '.6',
                '&:hover': {
                  opacity: '.8',
                },
              }}
            />
          </IconButton>
        </Stack>
      )

    // @ts-ignore
    case 'phone':
      const phone = formatPhoneNumber((request as CustomerPersonalInfo).phone)
      return (
        <Stack alignItems={'center'} direction='row' spacing={'8px'}>
          <Typography variant='body1'>{phone}</Typography>
          <IconButton
            onClick={() => {
              copy(phone as string)
            }}
            sx={{ padding: '2px' }}
          >
            <Box
              component='img'
              src={getImageSrc('copy')}
              alt='Скопировать'
              sx={{
                width: '20px',
                height: '20px',
                opacity: '.6',
                '&:hover': {
                  opacity: '.8',
                },
              }}
            />
          </IconButton>
        </Stack>
      )

    // @ts-ignore
    case 'fullAddress':
      const address = formatAddress(
        (request as CustomerPersonalInfo).fullAddress,
        false,
      )
      return (
        <Stack alignItems={'center'} direction='row' spacing={'8px'}>
          <Typography variant='body1'>{address}</Typography>
          <IconButton
            onClick={() => {
              copy(address)
            }}
            sx={{ padding: '2px' }}
          >
            <Box
              component='img'
              src={getImageSrc('copy')}
              alt='Скопировать'
              sx={{
                width: '20px',
                height: '20px',
                opacity: '.6',
                '&:hover': {
                  opacity: '.8',
                },
              }}
            />
          </IconButton>
        </Stack>
      )
    case 'receiverSerialNumber':
      return (request as RequestType)[key] ? (
        <Stack alignItems={'center'} direction='row' spacing={'8px'}>
          <Typography variant='body1'>
            {formatSN((request as RequestType)[key] as string)}
          </Typography>
          <IconButton
            onClick={() => {
              copy(String((request as RequestType)[key]))
            }}
            sx={{ padding: '2px' }}
          >
            <Box
              component='img'
              src={getImageSrc('copy')}
              alt='Скопировать'
              sx={{
                width: '20px',
                height: '20px',
                opacity: '.6',
                '&:hover': {
                  opacity: '.8',
                },
              }}
            />
          </IconButton>
        </Stack>
      ) : (
        <Box width={'150px'} height={'20px'} />
      )

    case 'equipmentId':
      return (request as RequestType)[key] ? (
        <Stack alignItems={'center'} direction='row' spacing={'8px'}>
          <Typography variant='body1'>
            {formatEquipmentId((request as RequestType)[key] as string)}
          </Typography>
          <IconButton
            onClick={() => {
              copy(String((request as RequestType)[key]))
            }}
            sx={{ padding: '2px' }}
          >
            <Box
              component='img'
              src={getImageSrc('copy')}
              alt='Скопировать'
              sx={{
                width: '20px',
                height: '20px',
                opacity: '.6',
                '&:hover': {
                  opacity: '.8',
                },
              }}
            />
          </IconButton>
        </Stack>
      ) : (
        <Box width={'150px'} height={'20px'} />
      )

    case 'receiverProduct':
      return (request as RequestType)[key] ? (
        <Stack alignItems={'center'} direction='row' spacing={'8px'}>
          <Typography variant='body1'>
            {(request as RequestType).receiverProduct}
          </Typography>
          <IconButton
            onClick={() => {
              copy(`${(request as RequestType).receiverProduct}`)
            }}
            sx={{ padding: '2px' }}
          >
            <Box
              component='img'
              src={getImageSrc('copy')}
              alt='Скопировать'
              sx={{
                width: '20px',
                height: '20px',
                opacity: '.6',
                '&:hover': {
                  opacity: '.8',
                },
              }}
            />
          </IconButton>
        </Stack>
      ) : (
        <Box width={'150px'} height={'20px'} />
      )

    case 'categories':
      const categories = (request as CustomerPersonalInfo).categories
      const formattedCats = getComp('CategoriesStore')
        ?.formatCategory(categories)
        ?.split(', ')

      // @ts-ignore
      return formattedCats[0] ? (
        <Stack alignItems={'center'} direction='row' spacing={'8px'}>
          <Typography variant='body1'>
            {`${formattedCats[0]}${formattedCats.length > 1 ? '...' : ''}`}
          </Typography>
        </Stack>
      ) : (
        <Stack alignItems={'center'} direction='row' spacing={'8px'}>
          <Typography variant='body1'>Не указана</Typography>
        </Stack>
      )

    default:
      return (request as RequestType)[key] ? (
        <Stack alignItems={'center'} direction='row' spacing={'4px'}>
          <Typography variant='body1'>
            {(request as RequestType)[key]}
          </Typography>
          <IconButton
            onClick={() => {
              copy(String((request as RequestType)[key]))
            }}
            sx={{ padding: '2px' }}
          >
            <Box
              component='img'
              src={getImageSrc('copy')}
              alt='Скопировать'
              sx={{
                width: '20px',
                height: '20px',
                opacity: '.6',
                '&:hover': {
                  opacity: '.8',
                },
              }}
            />
          </IconButton>
        </Stack>
      ) : (
        <Box width={'150px'} height={'20px'} />
      )
  }
}
